import {MOON_500} from '@wandb/weave/common/css/color.styles';
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
import {
  constString,
  opProjectArtifact,
  opRootProject,
  OutputNode,
} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import React, {useCallback} from 'react';
import {SingleValue} from 'react-select';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup, Table} from 'semantic-ui-react';

import {
  EventTriggeringConditionType,
  FetchProjectTriggersQueryResult,
  Trigger,
  TriggeredActionType,
  TriggerScopeType,
  useCreateFilterTriggerMutation,
  useDeleteTriggerMutation,
  useEntityQuery,
  useFetchProjectTriggersQuery,
} from '../../generated/graphql';
import {WANDB_SYSTEM_AUTOMATION_NAME} from '../Launch/utils';
import {SlackIntegrationsTable} from '../SlackIntegration/SlackIntegrationsTable';
import {normalizeSlackChannelName} from '../SlackIntegration/utils';
import {UserSettingsPlaceholder} from '../UserSettings/UserSettingsPlaceholder';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {useEntityIntegrations} from './hooks';
import {SectionPlaceholder} from './SectionPlaceholder';
import * as S from './styles';
import {
  getDescription,
  getNotificationTriggers,
  getTitleAndMessage,
} from './utils';

type Slack = {
  id: string;
  type: 'slack';
  teamName: string;
  channelName: string;
};

export type Integration = Slack; // Make this a union type when we support other integrations (i.e email)

export type EntityIntegrations = {
  loading: boolean;
  entityName: string;
  data: Integration[];
  refetch: () => Promise<any>;
};

type ArtifactCollectionNotificationsOuterProps = {
  entityName: string;
  projectName: string;
  artifactCollectionName: string;
};

type ArtifactCollectionNotificationsProps = {
  integrations: EntityIntegrations;
  artifactCollectionID: string;
  artifactCollectionName: string;
  triggersQueryResult: FetchProjectTriggersQueryResult;
  triggers?: Trigger[];
};

const ArtifactCollectionNotificationsOuter = (
  props: ArtifactCollectionNotificationsOuterProps
) => {
  const {entityName, projectName, artifactCollectionName} = props;
  const entityIntegrations = useEntityIntegrations(entityName);

  const triggersQuery = useFetchProjectTriggersQuery({
    variables: {projectName, entityName},
  });

  const artifactCollectionNode = opProjectArtifact({
    project: opRootProject({
      entityName: constString(entityName),
      projectName: constString(projectName),
    }),
    artifactName: constString(artifactCollectionName),
  }) as OutputNode<'artifact'>;

  const artifactCollection = useNodeValue(artifactCollectionNode);

  if (
    entityIntegrations.loading ||
    triggersQuery.loading ||
    triggersQuery.data == null ||
    artifactCollection.loading
  ) {
    return <SectionPlaceholder />;
  }

  return (
    <ArtifactCollectionNotificationsInner
      integrations={entityIntegrations}
      artifactCollectionID={artifactCollection.result.id}
      triggersQueryResult={triggersQuery}
      artifactCollectionName={artifactCollectionName}
    />
  );
};

const ArtifactCollectionNotificationsInner: React.FC<
  ArtifactCollectionNotificationsProps
> = ({
  integrations,
  artifactCollectionID,
  triggersQueryResult,
  artifactCollectionName,
}) => {
  // we need this for the SlackIntegrations component below
  // TODO: refactor SlackIntegrations so that this entityQuery is not required
  const entityQuery = useEntityQuery({
    variables: {
      name: integrations.entityName,
    },
    fetchPolicy: 'cache-and-network',
  });

  if (entityQuery.loading || !entityQuery.data) {
    return <UserSettingsPlaceholder />;
  }

  return (
    <S.SettingsWrapper>
      <S.SettingsSectionContainer>
        <S.SectionHeader as="h2">Automated Notifications</S.SectionHeader>
        <p style={{color: 'darkgrey'}}>
          Notify the team when changes happen in the model registry.
        </p>
        <S.NotificationsTable>
          <NotificationsTable
            integrations={integrations}
            artifactCollectionID={artifactCollectionID}
            triggersQueryResult={triggersQueryResult}
            artifactCollectionName={artifactCollectionName}
          />
        </S.NotificationsTable>
        <SlackIntegrationsTable
          entity={entityQuery.data.entity}
          entityRefetch={entityQuery.refetch}
          integrationReason="Configure a Slack integration so we can send you alerts."
          hideCreate={true}
          hideDisconnect={true}
        />
      </S.SettingsSectionContainer>
    </S.SettingsWrapper>
  );
};

type NotificationTriggerRowProps = {
  eventType: EventTriggeringConditionType;
  eventDescription: string;
  // slackIntegrations: Slack[];
  slackIntegration: Slack | undefined;
  artifactCollectionID: string;
  artifactCollectionName: string;
  triggerID?: string;
  enabled: boolean;
  entityRefetch: any;
  triggersQueryResult: FetchProjectTriggersQueryResult;
  isRow: boolean;
};

type NotificationsTableProps = {
  integrations: EntityIntegrations;
  artifactCollectionID: string;
  artifactCollectionName: string;
  triggersQueryResult: FetchProjectTriggersQueryResult;
};

const NotificationsTable: React.FC<NotificationsTableProps> = ({
  integrations,
  artifactCollectionID,
  triggersQueryResult,
  artifactCollectionName,
}) => {
  const slackIntegrations = React.useMemo(() => {
    return integrations.data.filter(i => i.type === 'slack');
  }, [integrations]);

  // Fetch all notification triggers for this artifact collection
  const notificationTriggers = getNotificationTriggers(
    artifactCollectionID,
    triggersQueryResult
  );
  const supportedEvents = [EventTriggeringConditionType.LinkModel];

  type triggerEnabled = {
    enabled: boolean;
    id?: string;
    integrationId?: string;
  };

  const enabledNotifications = Object.fromEntries(
    Object.values(EventTriggeringConditionType).map(e => [e, {enabled: false}])
  ) as {
    [T in EventTriggeringConditionType]: triggerEnabled;
  };

  // Note: The backend currently limits one LINK_MODEL notification trigger
  // per artifact collection, and we are relying on this assumption here.
  for (const n of notificationTriggers) {
    const current = n.triggeringCondition.eventType;
    if (n.triggeredAction.__typename === 'NotificationTriggeredAction') {
      enabledNotifications[current] = {
        enabled: true,
        id: n.id,
        integrationId: n.triggeredAction.integration.id,
      };
    }
  }

  return (
    <Table className={'alerts--table'} basic={'very'}>
      <Table.Header>
        <Table.Row>
          <S.HeaderCellBold>Events</S.HeaderCellBold>
          <S.HeaderCellBold>Slack</S.HeaderCellBold>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {supportedEvents.map((e, _) => {
          return (
            slackIntegrations.length > 0
              ? slackIntegrations
              : [slackIntegrations[0]]
          ).map(integration => (
            <NotificationTriggerRow
              key={e}
              eventType={e}
              eventDescription={getDescription(artifactCollectionName, e)}
              artifactCollectionID={artifactCollectionID}
              enabled={
                enabledNotifications[e]?.enabled &&
                enabledNotifications[e]?.integrationId === integration?.id
              }
              triggerID={enabledNotifications[e]?.id}
              slackIntegration={integration}
              entityRefetch={integrations.refetch}
              triggersQueryResult={triggersQueryResult}
              artifactCollectionName={artifactCollectionName}
              isRow={true}
            />
          ));
        })}
      </Table.Body>
    </Table>
  );
};

const slackChannelSelector = (
  slackIntegrations: Slack[],
  enabled: boolean,
  onSelect: (integrationId: string) => void
) => {
  if (slackIntegrations.length < 1) {
    return null;
  }

  return (
    <Select<Slack>
      options={slackIntegrations}
      defaultValue={slackIntegrations[0]}
      onChange={(c: SingleValue<Slack>) => {
        if (c) {
          onSelect(c.id);
        }
      }}
      isDisabled={!enabled}
      formatOptionLabel={(c: Slack) => {
        return c?.channelName;
      }}
    />
  );
};

export const NotificationTriggerRow: React.FC<NotificationTriggerRowProps> = ({
  eventType,
  eventDescription,
  slackIntegration,
  artifactCollectionID,
  triggerID,
  enabled,
  entityRefetch,
  triggersQueryResult,
  artifactCollectionName,
  isRow,
}) => {
  const [createTriggerMutation] = useCreateFilterTriggerMutation();
  const [deleteTriggerMutation] = useDeleteTriggerMutation();

  const deleteNotificationTrigger = useCallback(async () => {
    if (triggerID != null) {
      await deleteTriggerMutation({
        variables: {triggerID},
      });
      await entityRefetch();
      await triggersQueryResult.refetch();
    }
    return null;
  }, [triggerID, deleteTriggerMutation, entityRefetch, triggersQueryResult]);

  const createNotificationTrigger = useCallback(async () => {
    if (slackIntegration == null) {
      return;
    }

    const {title, message} = getTitleAndMessage(
      eventType,
      artifactCollectionName
    );
    const actionPayload = {
      notificationActionInput: {
        integrationID: slackIntegration.id,
        title,
        message,
      },
    };
    try {
      await createTriggerMutation({
        variables: {
          name: WANDB_SYSTEM_AUTOMATION_NAME, // name is unique within scope
          triggeringEventType: eventType,
          scopeType: TriggerScopeType.ArtifactCollection,
          scopeID: artifactCollectionID,
          eventFilter: JSON.stringify({}),
          triggeredActionType: TriggeredActionType.Notification,
          triggeredActionConfig: actionPayload,
          enabled: true,
        },
      });
      await entityRefetch();
      await triggersQueryResult.refetch();
    } catch (error) {
      console.error(error);
      toast(
        'Something went wrong when trying to create this automated Slack notification.'
      );
    }
  }, [
    slackIntegration,
    artifactCollectionID,
    artifactCollectionName,
    createTriggerMutation,
    entityRefetch,
    eventType,
    triggersQueryResult,
  ]);

  return (
    <>
      {isRow ? (
        <Table.Row>
          <Table.Cell>{eventDescription}</Table.Cell>
          <Table.Cell>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <div>
                <SlackToggle
                  disabled={slackIntegration == null}
                  disabledReason={
                    'Connect Slack to trigger alerts for this event'
                  }
                  onToggleTrue={createNotificationTrigger}
                  onToggleFalse={deleteNotificationTrigger}
                  initialChecked={enabled}
                />
              </div>
              <div
                style={{
                  color: MOON_500,
                  marginRight: '6px',
                  marginLeft: '6px',
                  textAlign: 'right',
                }}>
                {slackIntegration?.channelName
                  ? normalizeSlackChannelName(slackIntegration.channelName)
                  : ''}
              </div>
            </div>
          </Table.Cell>
        </Table.Row>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
            }}>
            <div style={{marginLeft: '4px'}}>
              <SlackToggle
                disabled={slackIntegration == null}
                disabledReason={
                  'Connect Slack to trigger alerts for this event'
                }
                onToggleTrue={createNotificationTrigger}
                onToggleFalse={deleteNotificationTrigger}
                initialChecked={enabled}
              />
            </div>
            <div
              style={{
                color: MOON_500,
                marginRight: '6px',
                marginLeft: '6px',
                textAlign: 'right',
              }}>
              {slackIntegration?.channelName
                ? normalizeSlackChannelName(slackIntegration.channelName)
                : ''}
            </div>
            <div style={{marginLeft: '4px', display: 'flex'}}>
              {eventDescription}
            </div>
          </div>
        </>
      )}
    </>
  );
};

interface SlackToggleProps {
  initialChecked: boolean;
  disabled: boolean;
  disabledReason: string;
  onToggleTrue: () => Promise<any>;
  onToggleFalse: () => Promise<any>;
}

const SlackToggle: React.FC<SlackToggleProps> = ({
  disabled,
  disabledReason,
  onToggleTrue,
  onToggleFalse,
  initialChecked,
}) => {
  const [checked, setChecked] = React.useState(initialChecked);
  return (
    <Popup
      disabled={!disabled}
      content={disabledReason}
      on={'hover'}
      trigger={
        <>
          <S.Toggle
            toggle
            disabled={disabled}
            checked={checked}
            onChange={(e: any, data: any) => {
              if (data.checked) {
                onToggleTrue().then(() => {
                  toast('Notification created successfully!');
                });
              } else {
                onToggleFalse().then(() => {
                  toast('Removed automated notification.');
                });
              }
              setChecked(!checked);
            }}
          />
        </>
      }
    />
  );
};

export default ArtifactCollectionNotificationsOuter;
